/* FOM · painel contextual do no selecionado (Fase 1R — referencia visual).

   E aqui que o mapa deixa de ser desenho e vira ferramenta: o que se sabe, o
   que falta saber, de onde veio, e o que da para mudar.

   ESTRUTURA (progressive disclosure, como a referencia pede):
     cabecalho   nome + classificacao + origem
     abas        Visao geral · Conhecimento · Lacunas · Investigacoes
     visao geral Sobre o no · donut do estado do conhecimento · acoes rapidas
                 · conexoes diretas (pai / filhos / relacionadas)

   O QUE O PAINEL NAO INVENTA:
     · "Responsavel" nao existe no dominio — a referencia mostra, o produto
       nao tem o campo, e campo inventado e mentira bonita;
     · "Criar conhecimento" direto nao existe — conhecimento nasce de
       investigacao com evidencia (teto epistemologico). A acao aqui e
       INVESTIGAR, que e o caminho verdadeiro;
     · o donut conta o que o banco diz: know, lacunas e investigacoes abertas
       deste no. Nada de score inventado.

   Regras de dominio que continuam na tela:
     · E1/E2 exigem referencia de evidencia — o formulario cobra antes de
       enviar (a recusa final continua sendo do banco, C-02);
     · no da BASE e no do usuario sao visualmente distintos;
     · o que e lacuna aparece como lacuna, nao como campo vazio. */
import type { FomNode, Relation, StatusClass, NodeType, InvestigationResumo } from '../core/types.ts';
import { COR_ST, ROTULO_ST, ROTULO_TIPO, DESCRICAO_ST, rotuloTipo} from '../core/types.ts';
import { guiaDe, AVISO_GUIA, MARCA_AUSENTE, FONTES_GUIA } from '../dominio/guia-operacional.ts';

const STATUS: StatusClass[] = ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE'];
const TIPOS: NodeType[] = [
  'process', 'activity', 'func', 'unit', 'service', 'gest', 'system',
  'partner', 'network', 'modal', 'org', 'mgmt', 'case',
];

export type PainelOpcoes = {
  aoCriarFilho: (pai: FomNode) => void;
  aoEditar: (no: FomNode) => void;
  aoRemover: (no: FomNode) => void;
  aoLigarDe: (no: FomNode) => void;
  aoCentralizar: (externalId: string) => void;
  /* GATE 0062: acoes SOMENTE de relacao semantica (linhas vindas de
     `relacoes`, que tem id proprio) — NUNCA de pai/filho, que derivam de
     parent_external_id e nao sao relacao editavel (FOM-ARQ-0061 §8). */
  aoEditarRelacao?: (rel: Relation) => void;
  aoRemoverRelacao?: (rel: Relation) => void;
  /* A transicao central do produto: uma lacuna vira pergunta investigavel. */
  aoInvestigarLacuna: (no: FomNode, lacuna: string) => void;
  aoAbrirContrato: (no: FomNode) => void;
  aoAbrirInvestigacoes: (no: FomNode) => void;
  /* NEXT-GEN: Evidence Inspector a um clique (lacuna → pergunta → evidencia →
     resposta → classificacao → conhecimento) */
  aoAbrirEvidencia?: (no: FomNode) => void;
  /* conteudo do estado vazio: orientacao + estado do mapa, montado por quem
     tem acesso ao store — o painel so exibe */
  resumoVazio?: () => HTMLElement | null;
  /* investigacoes do no, para a aba e para o donut — busca real, cacheada por
     render de quem prove */
  investigacoesDe?: (externalId: string) => Promise<InvestigationResumo[]>;
  /* papel do usuario (servidor). A UI NAO autoriza nada: apenas explica o
     que o servidor recusaria — affordance visivel, desabilitada, com motivo. */
  papel?: () => string;
};

function texto(tag: string, classe: string, conteudo: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = classe;
  e.textContent = conteudo;
  return e;
}

function botaoEl(rotulo: string, classe: string, acao: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = classe;
  b.textContent = rotulo;
  b.addEventListener('click', acao);
  return b;
}


export function montarPainel(host: HTMLElement, opcoes: PainelOpcoes) {
  /* ACRESCENTA a classe; nao substitui. O host chega como `.lateral`, que e a
     SUPERFICIE do painel — fundo, borda, padding, rolagem e a regra responsiva
     que o esconde quando a gaveta abre. `className =` apagava tudo isso e o
     conteudo renderizava solto sobre o fundo da pagina. */
  host.classList.add('painel');

  /* cache das investigacoes por no: uma busca por selecao, nao por pintura */
  let invsDe: { id: string; lista: InvestigationResumo[] } | null = null;
  /* aba ativavel de fora (BRAIN VIEW clica numa particula -> aba certa) */
  let ativarAbaAtual: ((idx: number) => void) | null = null;

  function vazio(): void {
    host.innerHTML = '';
    host.classList.add('painel-vazio');
    const guia = opcoes.resumoVazio?.();
    if (guia) { host.appendChild(guia); return; }
    host.appendChild(texto('p', 'painel-dica',
      'Selecione um no do mapa para ver o que se sabe, o que falta e o que da para mudar.'));
  }

  /* ----------------------------------------- CONEXAO selecionada (0072) ----
     A linha do mapa e objeto de primeira classe: ao clica-la o painel
     descreve a relacao — ORIGEM, DESTINO, TIPO, NATUREZA, PAYLOAD, ESTADO — e
     oferece Editar/Remover (as mesmas rotas do gate 0062). Pai/filho nunca
     chegam aqui: hierarquia nao e relacao. Nada e inventado: campo ausente
     aparece como ausente. */
  function renderRelacao(rel: Relation, nos: FomNode[]): void {
    host.classList.remove('painel-vazio');
    host.innerHTML = '';
    host.classList.add('painel-conexao');
    const porId = new Map(nos.map((n) => [n.external_id, n]));
    const de = porId.get(rel.from);
    const para = porId.get(rel.to);
    const escreve = (opcoes.papel?.() ?? 'owner') !== 'viewer';

    const cab = document.createElement('header');
    cab.className = 'painel-cabecalho';
    const faixa = document.createElement('span');
    faixa.className = 'painel-faixa painel-faixa-conexao';
    cab.appendChild(faixa);
    const idBox = document.createElement('div');
    idBox.className = 'painel-idbox';
    idBox.appendChild(texto('div', 'painel-codigo', 'CONEXAO · ' + (rel.kind === 'flow' ? 'fluxo' : 'transversal')));
    idBox.appendChild(texto('h2', 'painel-titulo', rel.type));
    const chip = texto('span', 'chip-st chip-conexao', rel.origin === 'base' ? 'BASE congelada' : 'criada por usuario');
    idBox.appendChild(chip);
    cab.appendChild(idBox);
    host.appendChild(cab);

    const pontas = document.createElement('div');
    pontas.className = 'conexao-pontas';
    const ponta = (rotulo: string, id: string, no: FomNode | undefined): HTMLElement => {
      const d = document.createElement('div');
      d.className = 'conexao-ponta';
      d.appendChild(texto('span', 'painel-rotulo', rotulo));
      const b = botaoEl(no?.name ?? id, 'rel-alvo conexao-ponta-nome', () => opcoes.aoCentralizar(id));
      b.title = 'ir para ' + (no?.name ?? id);
      d.appendChild(b);
      d.appendChild(texto('span', 'painel-valor', no
        ? `${rotuloTipo(no.node_type) ?? 'tipo nao informado'} · ${ROTULO_ST[no.st]}${(no.layer === 'tobe' || no.st === 'TOBE') ? ' · TO-BE' : ''}`
        : 'no nao carregado neste mapa'));
      return d;
    };
    pontas.appendChild(ponta('Origem', rel.from, de));
    pontas.appendChild(texto('span', 'conexao-seta', '→'));
    pontas.appendChild(ponta('Destino', rel.to, para));
    host.appendChild(pontas);

    const sobre = document.createElement('div');
    sobre.className = 'sobre-no';
    const par = (rotulo: string, valor: string, classe = ''): HTMLElement => {
      const d = document.createElement('div');
      d.className = 'sobre-par' + (classe ? ' ' + classe : '');
      d.appendChild(texto('span', 'painel-rotulo', rotulo));
      d.appendChild(texto('span', 'painel-valor', valor));
      return d;
    };
    sobre.appendChild(par('Tipo da relacao', rel.type));
    sobre.appendChild(par('Natureza', rel.kind === 'flow' ? 'fluxo (sequencia)' : 'transversal'));
    sobre.appendChild(par('Origem do dado', rel.origin === 'base' ? 'BASE congelada' : 'USER'));
    const futura = !!(de && (de.layer === 'tobe' || de.st === 'TOBE')) || !!(para && (para.layer === 'tobe' || para.st === 'TOBE'));
    sobre.appendChild(par('Estado', futura ? 'toca uma ponta TO-BE · nunca conta como atual' : 'ativa · AS-IS'));
    sobre.appendChild(par('Ordem', String(rel.ordinal)));
    host.appendChild(sobre);

    const payload = document.createElement('div');
    payload.className = 'painel-fonte conexao-payload' + (rel.payload ? '' : ' conexao-payload-ausente');
    payload.appendChild(texto('span', 'painel-rotulo', 'Payload'));
    payload.appendChild(texto('span', 'painel-valor', rel.payload && rel.payload.trim() ? rel.payload : 'nao informado'));
    host.appendChild(payload);

    host.appendChild(texto('p', 'epist-nota', 'Hierarquia (pai/filho) nao e conexao e nao muda aqui. Origem e destino nao sao editaveis: para trocar as pontas, remova e conecte de novo.'));

    host.appendChild(texto('h3', 'painel-secao', 'Acoes'));
    const acoes = document.createElement('div');
    acoes.className = 'painel-acoes acoes-grade';
    const acao = (rotulo: string, classe: string, fn: () => void): void => {
      const b = botaoEl(rotulo, classe, fn);
      if (!escreve) { b.disabled = true; b.classList.add('botao-bloqueado'); b.title = 'Disponivel para Editor ou Owner.'; }
      acoes.appendChild(b);
    };
    if (opcoes.aoEditarRelacao) acao('Editar conexao', 'botao conexao-editar', () => opcoes.aoEditarRelacao!(rel));
    if (opcoes.aoRemoverRelacao) acao('Remover', 'botao botao-perigo conexao-remover', () => opcoes.aoRemoverRelacao!(rel));
    host.appendChild(acoes);

    const rodape = document.createElement('div');
    rodape.className = 'painel-rodape';
    rodape.appendChild(texto('span', '', rel.id.slice(0, 8)));
    rodape.appendChild(texto('span', '', 'Esc volta'));
    host.appendChild(rodape);
  }

  function render(no: FomNode | null, relacoes: Relation[], nos: FomNode[], relSelecionada: Relation | null = null): void {
    host.classList.remove('painel-conexao');
    if (relSelecionada) { renderRelacao(relSelecionada, nos); return; }
    if (!no) { vazio(); return; }
    host.classList.remove('painel-vazio');
    host.innerHTML = '';

    const porId = new Map(nos.map((n) => [n.external_id, n]));
    const nomeDe = (id: string): string => porId.get(id)?.name ?? id;

    /* ------------------------------------------------------- cabecalho --- */
    const cabecalho = document.createElement('header');
    cabecalho.className = 'painel-cabecalho';
    const faixa = document.createElement('span');
    faixa.className = 'painel-faixa';
    faixa.style.background = COR_ST[no.st];
    cabecalho.appendChild(faixa);
    const idBox = document.createElement('div');
    idBox.className = 'painel-idbox';
    if (no.code) idBox.appendChild(texto('div', 'painel-codigo', no.code));
    idBox.appendChild(texto('h2', 'painel-titulo', no.name));
    const chipSt = texto('span', 'chip-st', ROTULO_ST[no.st]);
    chipSt.style.setProperty('--cor', COR_ST[no.st]);
    idBox.appendChild(chipSt);
    cabecalho.appendChild(idBox);
    host.appendChild(cabecalho);

    const etiquetas = document.createElement('div');
    etiquetas.className = 'etiquetas';
    if (no.layer === 'tobe') etiquetas.appendChild(texto('span', 'etiqueta', 'TO-BE'));
    const etiquetaTipo = rotuloTipo(no.node_type);
    if (etiquetaTipo) etiquetas.appendChild(texto('span', 'etiqueta', etiquetaTipo));
    etiquetas.appendChild(no.origin === 'base'
      ? texto('span', 'etiqueta etiqueta-base', 'BASE congelada')
      : texto('span', 'etiqueta etiqueta-user', 'criado por voce'));
    host.appendChild(etiquetas);

    /* ------------------------------------- NEXT-GEN: cabecalho epistemico --
       As quatro perguntas antes de "Sobre o no": o que SABEMOS (E1/E2), o
       que ACREDITAMOS (E3/E4), o que NAO SABEMOS (lacunas) e o que
       PLANEJAMOS (TO-BE) — no + subarvore, dado real, nunca score. */
    {
      const filhosDe = new Map<string, FomNode[]>();
      for (const n of nos) {
        if (n.removed_at || !n.parent_external_id) continue;
        const l = filhosDe.get(n.parent_external_id) ?? [];
        l.push(n);
        filhosDe.set(n.parent_external_id, l);
      }
      const sub: FomNode[] = [];
      const pilha = [...(filhosDe.get(no.external_id) ?? [])];
      let guarda = 0;
      while (pilha.length && guarda++ < 5000) {
        const n = pilha.pop()!;
        sub.push(n);
        for (const f of filhosDe.get(n.external_id) ?? []) pilha.push(f);
      }
      const todos = [no, ...sub];
      const contaKnow = (sts: string[]): number => todos.reduce((a, n) => a + n.know.filter((k) => sts.includes(k.st)).length, 0);
      const sabemos = contaKnow(['E1', 'E2']);
      const acreditamos = contaKnow(['E3', 'E4']);
      const lacunas = todos.reduce((a, n) => a + n.gaps.length, 0);
      const planejamos = todos.filter((n) => n.layer === 'tobe' || n.st === 'TOBE').length;
      const quad = document.createElement('div');
      quad.className = 'epist-quadrantes';
      const q = (classe: string, n: number, rotulo: string): void => {
        const d = document.createElement('div');
        d.className = 'epist-q ' + classe;
        d.appendChild(texto('b', '', String(n)));
        d.appendChild(texto('span', '', rotulo));
        quad.appendChild(d);
      };
      q('epist-q-k', sabemos, 'sabemos · E1/E2');
      q('epist-q-b', acreditamos, 'acreditamos · E3/E4');
      q('epist-q-n', lacunas, 'nao sabemos · lacunas');
      q('epist-q-p', planejamos, 'planejamos · TO-BE');
      host.appendChild(quad);
      /* distribuicao epistemica da subarvore (barra + legenda), como no
         Command Center: cor e dado, classe sempre escrita na legenda */
      if (todos.length > 1) {
        const dist = new Map<string, number>();
        for (const n of todos) dist.set(n.st, (dist.get(n.st) ?? 0) + 1);
        const barra = document.createElement('div');
        barra.className = 'ev-barra';
        const legenda = document.createElement('div');
        legenda.className = 'ev-legenda';
        for (const st of ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE', 'MIX'] as const) {
          const n = dist.get(st) ?? 0;
          if (!n) continue;
          const i = document.createElement('i');
          i.style.width = ((n / todos.length) * 100).toFixed(1) + '%';
          i.style.background = COR_ST[st];
          i.title = `${ROTULO_ST[st]} · ${n}`;
          barra.appendChild(i);
          const item = document.createElement('span');
          const ponto = document.createElement('i');
          ponto.style.background = COR_ST[st];
          item.appendChild(ponto);
          item.appendChild(document.createTextNode(`${st} ${n}`));
          legenda.appendChild(item);
        }
        host.appendChild(barra);
        host.appendChild(legenda);
      }
      host.appendChild(texto('p', 'epist-nota', sub.length
        ? `no + subarvore (${sub.length} descendente(s)) · TO-BE nunca conta como atual`
        : 'este no · TO-BE nunca conta como atual'));
      if (opcoes.aoAbrirEvidencia) {
        const cta = botaoEl('Evidencia · cadeia epistemologica', 'botao botao-principal painel-evidencia-cta',
          () => opcoes.aoAbrirEvidencia!(no));
        cta.title = 'lacuna → pergunta → evidencia → resposta → classificacao (com teto) → conhecimento';
        host.appendChild(cta);
      }
    }

    /* ------------------------------------------------------------ abas --- */
    const abas = document.createElement('nav');
    abas.className = 'painel-abas';
    abas.setAttribute('role', 'tablist');
    const paineis = document.createElement('div');
    paineis.className = 'painel-paineis';

    const conteudoGeral = document.createElement('section');
    const conteudoKnow = document.createElement('section');
    const conteudoGaps = document.createElement('section');
    const conteudoInvs = document.createElement('section');
    for (const s of [conteudoGeral, conteudoKnow, conteudoGaps, conteudoInvs]) {
      s.className = 'painel-aba-corpo';
      s.setAttribute('role', 'tabpanel');
      paineis.appendChild(s);
    }

    const defAbas: { rotulo: string; corpo: HTMLElement; badge?: number }[] = [
      { rotulo: 'Visao geral', corpo: conteudoGeral },
      { rotulo: 'Conhecimento', corpo: conteudoKnow, badge: no.know.length },
      { rotulo: 'Lacunas', corpo: conteudoGaps, badge: no.gaps.length },
      { rotulo: 'Investigacoes', corpo: conteudoInvs },
    ];
    const botoesAba: HTMLButtonElement[] = [];
    const ativar = (idx: number): void => {
      defAbas.forEach((a, i) => {
        a.corpo.hidden = i !== idx;
        botoesAba[i]!.classList.toggle('painel-aba-ativa', i === idx);
        botoesAba[i]!.setAttribute('aria-selected', String(i === idx));
      });
    };
    ativarAbaAtual = ativar;
    defAbas.forEach((a, i) => {
      const b = document.createElement('button');
      b.className = 'painel-aba';
      b.setAttribute('role', 'tab');
      b.textContent = a.rotulo;
      if (a.badge !== undefined && a.badge > 0) {
        b.appendChild(texto('span', 'painel-aba-badge', String(a.badge)));
      }
      b.addEventListener('click', () => ativar(i));
      botoesAba.push(b);
      abas.appendChild(b);
    });
    host.appendChild(abas);
    host.appendChild(paineis);

    /* ------------------------------------------------------ visao geral -- */
    /* Sobre o no — so campos que EXISTEM no dominio */
    const sobre = document.createElement('div');
    sobre.className = 'sobre-no';
    const par = (rotulo: string, valor: string): HTMLElement => {
      const d = document.createElement('div');
      d.className = 'sobre-par';
      d.appendChild(texto('span', 'painel-rotulo', rotulo));
      d.appendChild(texto('span', 'painel-valor', valor));
      return d;
    };
    /* EXPERIENCIA UNIVERSAL (gate 0072): as quatro linhas existem para
       QUALQUER no. Campo ausente aparece como ausente — declarar a lacuna e
       informacao; omitir a linha faria a experiencia mudar de forma conforme
       o dado, que e exatamente o que nao pode acontecer. */
    sobre.appendChild(par('Origem', no.origin === 'base' ? 'BASE' : 'USER'));
    sobre.appendChild(par('Classificacao', ROTULO_ST[no.st]));
    sobre.appendChild(par('Tipo', rotuloTipo(no.node_type) ?? 'nao informado'));
    sobre.appendChild(par('Atualizado', no.updated_at.slice(0, 16).replace('T', ' ')));
    conteudoGeral.appendChild(sobre);

    if (no.summary) conteudoGeral.appendChild(texto('p', 'painel-resumo', no.summary));
    if (no.src) {
      const s = document.createElement('div');
      s.className = 'painel-fonte';
      s.appendChild(texto('span', 'painel-rotulo', 'Fonte declarada'));
      s.appendChild(texto('span', 'painel-valor', no.src));
      conteudoGeral.appendChild(s);
    }

    /* ----------------------------------------------- guia operacional --- */
    /* So para etapas que a documentacao vigente cobre. Tudo aqui e
       DOCUMENTADO (E2) e carrega fonte por item — o observado continua nas
       abas Conhecimento/Lacunas. Lacuna documental ([DEFINIR]) nao vira
       regra: vira pergunta com o botao Investigar do mecanismo existente. */
    const guia = guiaDe(no.external_id);
    if (guia) {
      const caixa = document.createElement('section');
      caixa.className = 'guia-operacional';
      const topo = document.createElement('div');
      topo.className = 'guia-topo';
      topo.appendChild(texto('h3', 'painel-secao', 'Guia operacional'));
      topo.appendChild(texto('span', 'guia-selo', 'documentado · E2'));
      caixa.appendChild(topo);
      caixa.appendChild(texto('p', 'guia-aviso', AVISO_GUIA));

      const itemLinha = (item: { t: string; fonte: string }): HTMLElement => {
        const li = document.createElement('li');
        li.className = 'guia-item';
        li.appendChild(texto('span', 'guia-texto', item.t));
        li.appendChild(texto('span', 'guia-fonte', item.fonte));
        return li;
      };
      const parAberto = (rotulo: string, item: { t: string; fonte: string }): void => {
        const d = document.createElement('div');
        d.className = 'guia-par';
        d.appendChild(texto('span', 'painel-rotulo', rotulo));
        const ul = document.createElement('ul');
        ul.className = 'guia-lista';
        ul.appendChild(itemLinha(item));
        d.appendChild(ul);
        caixa.appendChild(d);
      };
      /* PROGRESSIVE DISCLOSURE (1Y.1): abrir um no coberto nao pode despejar
         uma parede de texto. O guia entra como UMA linha-resumo ("como esta
         etapa funciona - N orientacoes - fonte") e o corpo inteiro vive num
         unico expansor. Lacunas documentais e divergencias — o que MERECE
         atencao — continuam sempre visiveis, abaixo. */
      const corpoGuia = document.createElement('details');
      corpoGuia.className = 'guia-corpo';
      const totalItens = 2 + guia.precisaExistir.length + guia.entrega.length
        + guia.registros.length + guia.atencao.length;
      const fontePrincipal = (guia.oQueE.fonte.split(' ')[0] ?? '').replace(/[^A-Za-z0-9-]/g, '');
      const sumario = document.createElement('summary');
      sumario.className = 'guia-sumario';
      sumario.appendChild(texto('span', 'guia-sumario-titulo', 'Como esta etapa funciona'));
      sumario.appendChild(texto('span', 'guia-sumario-meta',
        totalItens + ' orientacoes · fonte ' + (fontePrincipal || 'documentada')));
      corpoGuia.appendChild(sumario);
      const dentro = document.createElement('div');
      dentro.className = 'guia-corpo-dentro';
      corpoGuia.appendChild(dentro);

      const dobra = (rotulo: string, itens: { t: string; fonte: string }[], aberto = false): void => {
        if (!itens.length) return;
        const det = document.createElement('details');
        det.className = 'guia-dobra';
        if (aberto) det.open = true;
        const sum = document.createElement('summary');
        sum.textContent = rotulo;
        sum.appendChild(texto('span', 'guia-n', String(itens.length)));
        det.appendChild(sum);
        const ul = document.createElement('ul');
        ul.className = 'guia-lista';
        for (const item of itens) ul.appendChild(itemLinha(item));
        det.appendChild(ul);
        dentro.appendChild(det);
      };
      const parDentro = (rotulo: string, item: { t: string; fonte: string }): void => {
        const d = document.createElement('div');
        d.className = 'guia-par';
        d.appendChild(texto('span', 'painel-rotulo', rotulo));
        const ul = document.createElement('ul');
        ul.className = 'guia-lista';
        ul.appendChild(itemLinha(item));
        d.appendChild(ul);
        dentro.appendChild(d);
      };

      parAberto('O que e', guia.oQueE);
      parDentro('Quem participa', guia.quem);
      dobra('O que precisa existir', guia.precisaExistir, true);
      dobra('O que deve ser entregue', guia.entrega);
      parDentro('O que acontece depois', guia.depois);
      dobra('Documentos e registros', guia.registros);
      dobra('Pontos de atencao', guia.atencao);
      caixa.appendChild(corpoGuia);

      /* Divergencia documentado × observado — os dois lados com fonte, e a
         saida e uma PERGUNTA. A interface nao conclui ("violada", "NC"):
         quem transforma divergencia em veredito e investigacao + decisao. */
      for (const div of guia.divergencias ?? []) {
        const bloco = document.createElement('div');
        bloco.className = 'guia-divergencia';
        bloco.appendChild(texto('h4', 'guia-div-titulo', 'Divergencia documentado × observado'));
        const lado = (rotulo: string, classe: string, item: { t: string; fonte: string }): HTMLElement => {
          const d = document.createElement('div');
          d.className = 'guia-div-lado';
          d.appendChild(texto('span', 'guia-div-rotulo ' + classe, rotulo));
          d.appendChild(texto('span', 'guia-texto', item.t));
          d.appendChild(texto('span', 'guia-fonte', item.fonte));
          return d;
        };
        bloco.appendChild(lado('Documentado · E2', 'guia-div-e2', div.documentado));
        bloco.appendChild(lado('Observado · E1', 'guia-div-e1', div.observado));
        const pe = document.createElement('div');
        pe.className = 'guia-div-pe';
        pe.appendChild(texto('span', 'guia-div-status', 'divergencia — merece investigacao, nao conclusao'));
        pe.appendChild(botaoEl('Investigar', 'gap-investigar',
          () => opcoes.aoInvestigarLacuna(no, div.pergunta)));
        bloco.appendChild(pe);
        caixa.appendChild(bloco);
      }

      if (guia.lacunasDocumentais.length) {
        const bloco = document.createElement('div');
        bloco.className = 'guia-ausentes';
        bloco.appendChild(texto('h4', 'guia-ausente-titulo', MARCA_AUSENTE));
        const ul = document.createElement('ul');
        ul.className = 'guia-lista';
        for (const lac of guia.lacunasDocumentais) {
          const li = document.createElement('li');
          li.className = 'guia-item guia-item-ausente';
          li.appendChild(texto('span', 'guia-texto', lac.pergunta));
          li.appendChild(texto('span', 'guia-fonte', lac.fonte));
          li.appendChild(botaoEl('Investigar', 'gap-investigar',
            () => opcoes.aoInvestigarLacuna(no, lac.pergunta)));
          ul.appendChild(li);
        }
        bloco.appendChild(ul);
        caixa.appendChild(bloco);
      }

      dobra('Documentacao (fontes deste guia)', FONTES_GUIA);
      conteudoGeral.appendChild(caixa);
    }

    /* NEXT-GEN: o "estado do conhecimento" e o cabecalho epistemico (quatro
       perguntas + barra), no topo do painel. O donut, que dizia o mesmo com
       outra forma, saiu: um numero, um lugar. A contagem de investigacoes
       abertas continua na aba Investigacoes. */

    /* Acoes rapidas — rotulos ESTAVEIS (testes e roteiros dependem deles).
       Para viewer, a affordance continua VISIVEL, porem desabilitada com o
       motivo — permissao controla acao, nao esconde capacidade (1AD.1). */
    const escreve = (opcoes.papel?.() ?? 'owner') !== 'viewer';
    const botaoAcao = (rotulo: string, classe: string, acao: () => void): HTMLButtonElement => {
      const b = botaoEl(rotulo, classe, acao);
      if (!escreve) {
        b.disabled = true;
        b.classList.add('botao-bloqueado');
        b.title = 'Disponivel para Editor ou Owner.';
      }
      return b;
    };
    conteudoGeral.appendChild(texto('h3', 'painel-secao', 'Acoes rapidas'));
    const acoes = document.createElement('div');
    acoes.className = 'painel-acoes acoes-grade';
    acoes.appendChild(botaoAcao('+ no filho', 'botao', () => opcoes.aoCriarFilho(no)));
    acoes.appendChild(botaoAcao('Editar', 'botao', () => opcoes.aoEditar(no)));
    acoes.appendChild(botaoAcao('Conectar a…', 'botao', () => opcoes.aoLigarDe(no)));
    if (no.origin === 'user') {
      acoes.appendChild(botaoAcao('Ocultar', 'botao botao-perigo', () => opcoes.aoRemover(no)));
    }
    acoes.appendChild(botaoAcao('Investigacao', 'botao botao-camada', () => opcoes.aoAbrirInvestigacoes(no)));
    acoes.appendChild(botaoAcao('Contrato', 'botao botao-camada', () => opcoes.aoAbrirContrato(no)));
    conteudoGeral.appendChild(acoes);

    /* Conexoes diretas: pai / filhos / relacionadas — tudo clicavel */
    const pai = no.parent_external_id ? porId.get(no.parent_external_id) : null;
    const filhos = nos.filter((n) => n.parent_external_id === no.external_id);
    const doNo = relacoes.filter((r) => r.from === no.external_id || r.to === no.external_id);
    conteudoGeral.appendChild(texto('h3', 'painel-secao',
      `Conexoes diretas · ${(pai ? 1 : 0) + filhos.length + doNo.length}`));
    const conexoes = document.createElement('ul');
    conexoes.className = 'lista-rel';
    const linhaConexao = (direcao: string, alvoId: string, tipo: string): HTMLElement => {
      const li = document.createElement('li');
      li.appendChild(texto('span', 'rel-direcao', direcao));
      li.appendChild(botaoEl(nomeDe(alvoId), 'rel-alvo', () => opcoes.aoCentralizar(alvoId)));
      li.appendChild(texto('span', 'rel-tipo', tipo));
      return li;
    };
    if (pai) conexoes.appendChild(linhaConexao('↑', pai.external_id, 'pai'));
    for (const f of filhos.slice(0, 4)) {
      conexoes.appendChild(linhaConexao('↓', f.external_id, 'filho'));
    }
    if (filhos.length > 4) {
      conexoes.appendChild(texto('li', 'rel-mais', `… e mais ${filhos.length - 4} filho(s)`));
    }
    for (const r of doNo) {
      const outro = r.from === no.external_id ? r.to : r.from;
      const direcao = r.from === no.external_id ? '→' : '←';
      const li = linhaConexao(direcao, outro, r.type);
      /* A distincao e pelo MODELO, nao pelo texto: so o que veio da lista de
         relacoes recebe Editar/Remover. Pai/filho ficam sem acoes — o Owner
         nao pode ler "↑ pai" como conexao editavel (FOM-ARQ-0061 §12.10). */
      li.classList.add('rel-semantica');
      const acoesRel = document.createElement('span');
      acoesRel.className = 'rel-acoes';
      const acaoRel = (rotulo: string, classe: string, fn: (rel: Relation) => void): void => {
        const b = botaoEl(rotulo, classe, () => fn(r));
        if (!escreve) {
          b.disabled = true;
          b.classList.add('botao-bloqueado');
          b.title = 'Disponivel para Editor ou Owner.';
        }
        acoesRel.appendChild(b);
      };
      if (opcoes.aoEditarRelacao) acaoRel('Editar', 'rel-acao', opcoes.aoEditarRelacao);
      if (opcoes.aoRemoverRelacao) acaoRel('Remover', 'rel-acao rel-acao-perigo', opcoes.aoRemoverRelacao);
      if (acoesRel.childElementCount > 0) li.appendChild(acoesRel);
      conexoes.appendChild(li);
    }
    conteudoGeral.appendChild(conexoes);

    /* ----------------------------------------------------- conhecimento -- */
    if (!no.know.length) {
      conteudoKnow.appendChild(texto('p', 'estado-vazio-detalhe',
        'Nada consolidado ainda. Conhecimento nasce de investigacao com evidencia — nunca de opiniao.'));
    } else {
      const ul = document.createElement('ul');
      ul.className = 'lista-know';
      for (const k of no.know) {
        const li = document.createElement('li');
        const ponto = document.createElement('b');
        ponto.className = 'ponto';
        ponto.style.background = COR_ST[k.st];
        li.appendChild(ponto);
        /* o nivel epistemico ESCRITO por item — E1 e E3 nao podem depender de
           decodificar cor (auditoria 1X, item 5; principio: documentado !=
           observado precisa ser legivel) */
        const nivel = texto('span', 'chip-epist', ROTULO_ST[k.st]);
        nivel.style.setProperty('--cor', COR_ST[k.st]);
        li.appendChild(nivel);
        const corpoK = texto('span', 'know-texto', k.text);
        if (k.origin === 'user') {
          corpoK.appendChild(texto('span', 'know-origem', 'investigado'));
        }
        li.appendChild(corpoK);
        ul.appendChild(li);
      }
      conteudoKnow.appendChild(ul);
    }

    /* ---------------------------------------------------------- lacunas -- */
    if (!no.gaps.length) {
      conteudoGaps.appendChild(texto('p', 'estado-vazio-detalhe',
        'Nenhuma lacuna declarada. O que ninguem escreveu ainda pode existir — declarar lacuna e admitir o que falta.'));
    } else {
      const ul = document.createElement('ul');
      ul.className = 'lista-gap';
      for (const gap of no.gaps) {
        const li = document.createElement('li');
        li.appendChild(document.createTextNode(gap));
        li.appendChild(botaoEl('investigar', 'gap-investigar',
          () => opcoes.aoInvestigarLacuna(no, gap)));
        ul.appendChild(li);
      }
      conteudoGaps.appendChild(ul);
    }

    /* ----------------------------------------------------- investigacoes - */
    conteudoInvs.appendChild(texto('p', 'estado-vazio-detalhe', 'Carregando…'));
    const pintarInvs = (lista: InvestigationResumo[]): void => {
      conteudoInvs.textContent = '';
      const doNoInvs = lista.filter((i) => i.node_external_id === no.external_id);
      const abertas = doNoInvs.filter((i) => i.status !== 'encerrada').length;
      const badgeIdx = 3;
      const b = botoesAba[badgeIdx]!;
      b.querySelector('.painel-aba-badge')?.remove();
      if (doNoInvs.length) b.appendChild(texto('span', 'painel-aba-badge', String(doNoInvs.length)));
      if (!doNoInvs.length) {
        conteudoInvs.appendChild(texto('p', 'estado-vazio-detalhe',
          'Nenhuma pergunta aberta sobre este no.'));
      } else {
        /* abertas = agora; encerradas = historia (presente, mas discreta).
           "N investigacoes" nunca pode ler-se como "N problemas atuais". */
        const abertasInv = doNoInvs.filter((i) => i.status !== 'encerrada');
        const encerradasInv = doNoInvs.filter((i) => i.status === 'encerrada');
        const listaDe = (itens: InvestigationResumo[]): HTMLElement => {
          const ul = document.createElement('ul');
          ul.className = 'lista-inv';
          for (const inv of itens) {
            const li = document.createElement('li');
            li.className = 'inv-item inv-' + inv.status;
            li.appendChild(botaoEl(inv.code, 'inv-codigo', () => opcoes.aoAbrirInvestigacoes(no)));
            li.appendChild(texto('span', 'inv-pergunta', inv.question));
            li.appendChild(texto('span', 'inv-status inv-status-' + inv.status, inv.status));
            ul.appendChild(li);
          }
          return ul;
        };
        if (abertasInv.length) {
          conteudoInvs.appendChild(texto('h4', 'inv-grupo-titulo', 'Abertas · ' + abertasInv.length));
          conteudoInvs.appendChild(listaDe(abertasInv));
        } else {
          conteudoInvs.appendChild(texto('p', 'estado-vazio-detalhe',
            'Nenhuma pergunta aberta agora.'));
        }
        if (encerradasInv.length) {
          const hist = document.createElement('details');
          hist.className = 'inv-historico';
          const sum = document.createElement('summary');
          sum.textContent = 'Encerradas (historico) · ' + encerradasInv.length;
          hist.appendChild(sum);
          hist.appendChild(listaDe(encerradasInv));
          conteudoInvs.appendChild(hist);
        }
      }
      conteudoInvs.appendChild(botaoEl('Abrir investigacoes deste no', 'botao botao-camada',
        () => opcoes.aoAbrirInvestigacoes(no)));
    };
    if (opcoes.investigacoesDe) {
      if (invsDe && invsDe.id === no.external_id) {
        pintarInvs(invsDe.lista);
      } else {
        const idPedido = no.external_id;
        opcoes.investigacoesDe(idPedido)
          .then((lista) => {
            invsDe = { id: idPedido, lista };
            /* o usuario pode ter trocado de no enquanto a busca corria */
            if (host.querySelector('.painel-titulo')?.textContent === no.name) pintarInvs(lista);
          })
          .catch((err: { status?: number }) => {
            conteudoInvs.textContent = '';
            conteudoInvs.appendChild(texto('p', 'estado-vazio-detalhe',
              err?.status === 403
                ? 'Investigacoes sao camada privada — disponiveis para Editor ou Owner.'
                : 'Nao foi possivel carregar as investigacoes agora.'));
          });
      }
    } else {
      conteudoInvs.textContent = '';
      conteudoInvs.appendChild(botaoEl('Abrir investigacoes deste no', 'botao botao-camada',
        () => opcoes.aoAbrirInvestigacoes(no)));
    }

    ativar(0);

    /* ----------------------------------------------------------- rodape -- */
    const rodape = document.createElement('div');
    rodape.className = 'painel-rodape';
    rodape.appendChild(texto('span', '', no.external_id));
    rodape.appendChild(texto('span', '', 'atualizado ' + no.updated_at.slice(0, 19).replace('T', ' ')));
    host.appendChild(rodape);
  }

  const ABAS_POR_NOME = { visao: 0, conhecimento: 1, lacunas: 2, investigacoes: 3 } as const;
  return {
    render,
    renderRelacao,
    vazio,
    abrirAba(nome: keyof typeof ABAS_POR_NOME): void {
      ativarAbaAtual?.(ABAS_POR_NOME[nome]);
    },
  };
}

/* ============================== formularios (dialogos) ==================== */

export type DadosNo = {
  name: string;
  st: StatusClass;
  /* chave de um tipo do catalogo (sistema ou da organizacao) — gate 0073 */
  node_type?: string;
  summary?: string;
  src?: string;
};

/* Resultado de tentar criar um tipo: `ok` so quando o tipo foi realmente
   PERSISTIDO; senao, o motivo — que a tela escreve. */
export type ResultadoCriarTipo =
  | { ok: true; valor: string; rotulo: string }
  | { ok: false; motivo: string };

/* Sem um caminho de persistencia injetado, criar tipo e indisponivel e a tela
   DIZ isso — nunca finge que salvou. Em producao quem injeta e o main.ts, com
   a rota real de taxonomia (gate 0073). */
export const MOTIVO_TIPO_INDISPONIVEL =
  'Indisponivel: esta tela nao esta ligada ao catalogo de tipos. O nome digitado nao foi salvo.';
export async function criarTipoPadrao(_nome: string): Promise<ResultadoCriarTipo> {
  return { ok: false, motivo: MOTIVO_TIPO_INDISPONIVEL };
}

/* Um tipo como o seletor precisa dele. `escopo` explica de quem e o tipo —
   do produto ou desta organizacao — e a UX mostra isso ao lado do nome. */
export type OpcaoTipo = { key: string; label: string; scope?: 'system' | 'organization' };

export type OpcoesTipoForm = {
  /* catalogo ATIVO visivel a organizacao; se ausente, os 13 de sistema */
  tipos?: OpcaoTipo[];
  criarTipo?: (nome: string) => Promise<ResultadoCriarTipo> | ResultadoCriarTipo;
};

export function formularioNo(
  hostDialogo: HTMLElement,
  titulo: string,
  inicial: Partial<DadosNo>,
  aoConfirmar: (dados: DadosNo) => void,
  opcoesTipo?: OpcoesTipoForm,
): void {
  hostDialogo.innerHTML = '';
  hostDialogo.hidden = false;

  const form = document.createElement('form');
  form.className = 'dialogo';
  form.noValidate = true;
  form.innerHTML = `
    <h3></h3>
    <label class="campo"><span>Nome *</span>
      <input name="name" required maxlength="300" />
    </label>
    <div class="campo">
      <span>Classificacao *</span>
      <div class="escolha-st"></div>
      <p class="legenda-st-atual" aria-live="polite"></p>
      <details class="legenda-st">
        <summary>Ver legenda completa</summary>
        <dl class="legenda-st-lista"></dl>
      </details>
      <span class="ajuda">E1/E2 exigem evidencia com referencia. Sem evidencia, o teto e E3.</span>
    </div>
    <div class="campo campo-tipo">
      <span>Tipo</span>
      <div class="tipo-linha">
        <select name="node_type"><option value="">—</option></select>
        <button type="button" class="botao botao-adicionar-tipo" data-adicionar-tipo title="Criar uma categoria que a taxonomia atual nao contempla">+ Adicionar tipo</button>
      </div>
      <div class="novo-tipo" data-novo-tipo hidden>
        <label class="campo"><span>Nome do novo tipo</span>
          <input name="novo_tipo" maxlength="40" placeholder="ex.: Comite, Terminal, Agente…" />
        </label>
        <p class="ajuda novo-tipo-estado" data-novo-tipo-estado></p>
        <div class="dialogo-acoes novo-tipo-acoes">
          <button type="button" class="botao" data-novo-tipo-cancelar>Cancelar</button>
          <button type="button" class="botao-principal" data-novo-tipo-confirmar>Confirmar</button>
        </div>
      </div>
    </div>
    <label class="campo"><span>Resumo</span><textarea name="summary" rows="3" maxlength="4000"></textarea></label>
    <label class="campo campo-src"><span>Referencia da evidencia</span>
      <input name="src" maxlength="1000" placeholder="onde isso pode ser conferido" />
      <span class="ajuda"></span>
    </label>
    <p class="dialogo-erro" role="alert" hidden></p>
    <div class="dialogo-acoes">
      <button type="button" class="botao" data-cancelar>Cancelar</button>
      <button type="submit" class="botao-principal">Salvar</button>
    </div>
  `;
  form.querySelector('h3')!.textContent = titulo;

  const nome = form.querySelector<HTMLInputElement>('[name=name]')!;
  const tipo = form.querySelector<HTMLSelectElement>('[name=node_type]')!;
  const resumo = form.querySelector<HTMLTextAreaElement>('[name=summary]')!;
  const src = form.querySelector<HTMLInputElement>('[name=src]')!;
  const erro = form.querySelector<HTMLParagraphElement>('.dialogo-erro')!;
  const campoSrc = form.querySelector<HTMLElement>('.campo-src')!;
  const ajudaSrc = campoSrc.querySelector<HTMLElement>('.ajuda')!;

  /* O seletor vem do CATALOGO (gate 0073). Sem catalogo injetado, cai nos 13
     tipos de sistema — a tela nunca fica sem vocabulario. Tipos da
     organizacao aparecem marcados, para a pessoa saber o que e do produto e
     o que a casa criou. */
  const doCatalogo: OpcaoTipo[] = opcoesTipo?.tipos?.length
    ? opcoesTipo.tipos
    : TIPOS.map((t) => ({ key: t, label: ROTULO_TIPO[t], scope: 'system' as const }));
  const opcaoDe = (t: OpcaoTipo): HTMLOptionElement => {
    const o = document.createElement('option');
    o.value = t.key;
    o.textContent = t.scope === 'organization' ? `${t.label} · desta organizacao` : t.label;
    if (t.scope) o.dataset.escopo = t.scope;
    return o;
  };
  for (const t of doCatalogo) tipo.appendChild(opcaoDe(t));

  nome.value = inicial.name ?? '';
  /* o tipo atual do no pode nao estar mais no catalogo ativo (arquivado, ou
     catalogo ainda carregando): a opcao entra assim mesmo, marcada, para a
     edicao nunca trocar o tipo do no em silencio */
  if (inicial.node_type) {
    if (!doCatalogo.some((t) => t.key === inicial.node_type)) {
      const o = opcaoDe({ key: inicial.node_type, label: rotuloTipo(inicial.node_type) ?? inicial.node_type });
      o.dataset.foraDoCatalogo = '1';
      o.textContent += ' · fora do catalogo ativo';
      tipo.appendChild(o);
    }
    tipo.value = inicial.node_type;
  }

  /* "+ ADICIONAR TIPO" (gate 0072, ligado de verdade no 0073): a taxonomia
     evolui de forma governada — a pessoa nomeia a categoria que falta, o
     backend persiste no catalogo DESTA organizacao (nunca no vocabulario do
     sistema, nunca visivel a outro cliente) e devolve a chave estavel. Erro
     do servidor vira texto na tela; nada e salvo em silencio, nada entra no
     seletor sem ter sido persistido. */
  const abrirNovo = form.querySelector<HTMLButtonElement>('[data-adicionar-tipo]')!;
  const caixaNovo = form.querySelector<HTMLElement>('[data-novo-tipo]')!;
  const inputNovo = form.querySelector<HTMLInputElement>('[name=novo_tipo]')!;
  const estadoNovo = form.querySelector<HTMLElement>('[data-novo-tipo-estado]')!;
  const confirmarNovo = form.querySelector<HTMLButtonElement>('[data-novo-tipo-confirmar]')!;
  const fecharNovo = (): void => {
    caixaNovo.hidden = true;
    estadoNovo.textContent = '';
    estadoNovo.classList.remove('novo-tipo-indisponivel');
    inputNovo.value = '';
  };
  abrirNovo.addEventListener('click', () => {
    caixaNovo.hidden = false;
    estadoNovo.textContent = '';
    estadoNovo.classList.remove('novo-tipo-indisponivel');
    inputNovo.focus();
  });
  form.querySelector('[data-novo-tipo-cancelar]')!.addEventListener('click', fecharNovo);
  const tentarCriarTipo = (): void => {
    const nomeTipo = inputNovo.value.trim();
    estadoNovo.classList.remove('novo-tipo-indisponivel');
    if (!nomeTipo) { estadoNovo.textContent = 'De um nome ao novo tipo.'; return; }
    if (nomeTipo.length > 60) { estadoNovo.textContent = 'O nome do tipo tem no maximo 60 caracteres.'; return; }
    if (!/[a-zA-Z0-9]/.test(nomeTipo)) {
      estadoNovo.textContent = 'O nome precisa ter letras ou numeros.';
      estadoNovo.classList.add('novo-tipo-indisponivel');
      return;
    }
    const jaExiste = [...tipo.options].find((o) => o.textContent?.toLowerCase().startsWith(nomeTipo.toLowerCase()));
    if (jaExiste && jaExiste.value) {
      estadoNovo.textContent = `Ja existe o tipo "${jaExiste.textContent}". Escolha-o na lista ou use outro nome.`;
      estadoNovo.classList.add('novo-tipo-indisponivel');
      return;
    }
    estadoNovo.textContent = 'Criando…';
    confirmarNovo.disabled = true;
    void Promise.resolve((opcoesTipo?.criarTipo ?? criarTipoPadrao)(nomeTipo))
      .then((r) => {
        if (r.ok) {
          /* so agora entra no seletor: o tipo existe no banco */
          const o = opcaoDe({ key: r.valor, label: r.rotulo, scope: 'organization' });
          o.dataset.novo = '1';
          tipo.appendChild(o);
          tipo.value = r.valor;
          fecharNovo();
          return;
        }
        estadoNovo.textContent = r.motivo;
        estadoNovo.classList.add('novo-tipo-indisponivel');
      })
      .catch((e: Error) => {
        estadoNovo.textContent = 'Nao foi possivel criar o tipo: ' + e.message;
        estadoNovo.classList.add('novo-tipo-indisponivel');
      })
      .finally(() => { confirmarNovo.disabled = false; });
  };
  confirmarNovo.addEventListener('click', tentarCriarTipo);
  inputNovo.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Enter') { ev.preventDefault(); tentarCriarTipo(); }
  });
  resumo.value = inicial.summary ?? '';
  src.value = inicial.src ?? '';

  let st: StatusClass | null = inicial.st ?? null;
  const escolha = form.querySelector<HTMLElement>('.escolha-st')!;
  const pilulas: HTMLButtonElement[] = [];
  /* legenda contextual: a taxonomia OFICIAL (ROTULO_ST/DESCRICAO_ST) na
     propria tela — sem exigir hover, documentacao ou conhecimento previo */
  const legendaAtual = form.querySelector<HTMLElement>('.legenda-st-atual')!;
  const pintarLegenda = (): void => {
    legendaAtual.textContent = st
      ? ROTULO_ST[st] + ' — ' + DESCRICAO_ST[st]
      : 'Escolha uma classificacao — a legenda explica cada uma.';
  };
  const listaLegenda = form.querySelector<HTMLElement>('.legenda-st-lista')!;
  for (const s2 of STATUS) {
    const dt = document.createElement('dt');
    dt.textContent = ROTULO_ST[s2];
    const dd = document.createElement('dd');
    dd.textContent = DESCRICAO_ST[s2];
    listaLegenda.appendChild(dt);
    listaLegenda.appendChild(dd);
  }
  const atualizarExigencia = (): void => {
    const exige = st === 'E1' || st === 'E2';
    campoSrc.classList.toggle('exigido', exige);
    ajudaSrc.textContent = exige
      ? 'Obrigatoria para ' + st + ': o banco recusa classificacao observada sem referencia.'
      : 'Opcional para esta classificacao.';
  };
  for (const s of STATUS) {
    const p = document.createElement('button');
    p.type = 'button';
    p.className = 'pilula' + (s === st ? ' ativa' : '');
    p.style.setProperty('--cor', COR_ST[s]);
    p.textContent = s;
    p.title = ROTULO_ST[s];
    p.addEventListener('click', () => {
      st = s;
      for (const q of pilulas) q.classList.toggle('ativa', q === p);
      atualizarExigencia();
      pintarLegenda();
    });
    pilulas.push(p);
    escolha.appendChild(p);
  }
  atualizarExigencia();
  pintarLegenda();

  form.querySelector('[data-cancelar]')!.addEventListener('click', () => {
    hostDialogo.hidden = true;
    hostDialogo.innerHTML = '';
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    erro.hidden = true;
    if (!nome.value.trim()) { erro.textContent = 'O nome e obrigatorio.'; erro.hidden = false; return; }
    if (!st) { erro.textContent = 'Escolha a classificacao.'; erro.hidden = false; return; }
    if ((st === 'E1' || st === 'E2') && !src.value.trim()) {
      erro.textContent = `${st} e classificacao OBSERVADA: precisa da referencia da evidencia. `
        + 'Sem evidencia, o maximo honesto e E3 (relato) ou ND.';
      erro.hidden = false;
      return;
    }
    hostDialogo.hidden = true;
    hostDialogo.innerHTML = '';
    aoConfirmar({
      name: nome.value.trim(),
      st,
      ...(tipo.value ? { node_type: tipo.value } : {}),
      ...(resumo.value.trim() ? { summary: resumo.value.trim() } : { summary: '' }),
      ...(src.value.trim() ? { src: src.value.trim() } : { src: '' }),
    });
  });

  hostDialogo.appendChild(form);
  nome.focus();
}

/* O MESMO formulario para criar e para editar (GATE 0062): `edicao` presente
   liga o modo edicao — tipo e payload editaveis (os unicos campos que o PATCH
   do backend aceita); natureza exibida mas travada; e a limitacao deliberada
   fica ESCRITA na tela: origem/destino nao sao editaveis nesta operacao. */
export function formularioRelacao(
  hostDialogo: HTMLElement,
  de: FomNode,
  para: FomNode,
  aoConfirmar: (dados: { type: string; kind: 'trans' | 'flow'; payload?: string }) => void,
  edicao?: { type: string; kind: 'trans' | 'flow'; payload: string | null },
): void {
  hostDialogo.innerHTML = '';
  hostDialogo.hidden = false;
  const editando = edicao !== undefined;

  const form = document.createElement('form');
  form.className = 'dialogo';
  form.noValidate = true;
  form.innerHTML = `
    <h3>${editando ? 'Editar conexao' : 'Conectar'}</h3>
    <p class="dialogo-sub"><b></b> → <b></b></p>
    ${editando
      ? `<p class="ajuda aviso-pontas">Origem e destino nao sao editaveis nesta operacao.
         Para alterar as pontas, remova esta conexao e crie outra com "Conectar a…".</p>`
      : ''}
    <label class="campo"><span>O que essa conexao significa? *</span>
      <input name="type" required maxlength="200" placeholder="envia para, suporta, depende de…" />
    </label>
    <div class="campo">
      <span>Natureza${editando ? ' (nao editavel)' : ''}</span>
      <div class="escolha-st">
        <button type="button" data-kind="trans" class="pilula">transversal</button>
        <button type="button" data-kind="flow" class="pilula">fluxo</button>
      </div>
    </div>
    ${editando
      ? `<label class="campo"><span>Payload (opcional)</span>
           <textarea name="payload" rows="2" maxlength="2000"></textarea>
         </label>`
      : ''}
    <p class="dialogo-erro" role="alert" hidden></p>
    <div class="dialogo-acoes">
      <button type="button" class="botao" data-cancelar>Cancelar</button>
      <button type="submit" class="botao-principal">${editando ? 'Salvar' : 'Conectar'}</button>
    </div>
  `;
  /* nomes via textContent: dado do usuario nunca entra como HTML */
  const negritos = form.querySelectorAll<HTMLElement>('.dialogo-sub b');
  negritos[0]!.textContent = de.name;
  negritos[1]!.textContent = para.name;

  const tipo = form.querySelector<HTMLInputElement>('[name=type]')!;
  const payloadEl = form.querySelector<HTMLTextAreaElement>('[name=payload]');
  const erro = form.querySelector<HTMLParagraphElement>('.dialogo-erro')!;
  tipo.value = edicao?.type ?? '';
  if (payloadEl) payloadEl.value = edicao?.payload ?? '';

  let kind: 'trans' | 'flow' = edicao?.kind ?? 'trans';
  const pilulas = Array.from(form.querySelectorAll<HTMLButtonElement>('[data-kind]'));
  for (const p of pilulas) {
    p.classList.toggle('ativa', p.dataset.kind === kind);
    if (editando) {
      /* natureza nao muda no PATCH do backend — travada, nao escondida */
      p.disabled = true;
    } else {
      p.addEventListener('click', () => {
        kind = p.dataset.kind as 'trans' | 'flow';
        for (const q of pilulas) q.classList.toggle('ativa', q === p);
      });
    }
  }

  form.querySelector('[data-cancelar]')!.addEventListener('click', () => {
    hostDialogo.hidden = true;
    hostDialogo.innerHTML = '';
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!tipo.value.trim()) {
      erro.textContent = 'Descreva o que a conexao significa.';
      erro.hidden = false;
      return;
    }
    hostDialogo.hidden = true;
    hostDialogo.innerHTML = '';
    aoConfirmar({
      type: tipo.value.trim(),
      kind,
      ...(payloadEl ? { payload: payloadEl.value } : {}),
    });
  });

  hostDialogo.appendChild(form);
  tipo.focus();
}
